import React from "react"
import { Block } from "baseui/block"
import { useTranslation } from "react-i18next"
import { useActiveObject, useEditor } from "@agenteresolve/react"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import Scrollable from "~/components/Scrollable"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"

const PRESETS = [
  { id: "fade", labelKey: "presets.fade" },
  { id: "slide-up", labelKey: "presets.slideUp" },
  { id: "zoom", labelKey: "presets.zoom" },
  { id: "kinetic", labelKey: "presets.kinetic" },
]

const EASINGS = [
  { id: "quadratic-out", labelKey: "easings.quadraticOut" },
  { id: "quadratic-in-out", labelKey: "easings.quadraticInOut" },
  { id: "cubic-out", labelKey: "easings.cubicOut" },
  { id: "back-out", labelKey: "easings.backOut" },
  { id: "linear", labelKey: "easings.linear" },
]

const ANIM_TYPES = [
  { id: "none", labelKey: "types.none" },
  { id: "fade", labelKey: "types.fade" },
  { id: "slide", labelKey: "types.slide" },
  { id: "scale", labelKey: "types.scale" },
]

const DIRECTIONS = [
  { id: 90, labelKey: "directions.up" },
  { id: 270, labelKey: "directions.down" },
  { id: 180, labelKey: "directions.left" },
  { id: 0, labelKey: "directions.right" },
]

type AnimSpec = {
  type: "none" | "fade" | "slide" | "scale"
  duration: number
  delay: number
  easing: string
  direction: number
  distance: number
  startScale: number
  endScale: number
}

const DEFAULT_ENTER: AnimSpec = {
  type: "none",
  duration: 0.8,
  delay: 0,
  easing: "quadratic-out",
  direction: 90,
  distance: 50,
  startScale: 0,
  endScale: 100,
}

const DEFAULT_EXIT: AnimSpec = {
  type: "none",
  duration: 0.8,
  delay: 0,
  easing: "quadratic-out",
  direction: 270,
  distance: 50,
  startScale: 100,
  endScale: 0,
}

const chipStyle = (active: boolean): React.CSSProperties => ({
  cursor: "pointer",
  padding: "5px 10px",
  borderRadius: "999px",
  fontSize: "0.72rem",
  border: "1px solid",
  borderColor: active ? "#000" : "#d7d8e3",
  background: active ? "#000" : "#fff",
  color: active ? "#fff" : "#333",
  userSelect: "none",
})

const fieldRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  marginBottom: "6px",
  fontSize: "0.72rem",
}

const inputStyle: React.CSSProperties = {
  width: "86px",
  padding: "4px 6px",
  border: "1px solid #d7d8e3",
  borderRadius: "6px",
  fontSize: "0.72rem",
  background: "#fff",
}

function AnimEditor({
  title,
  value,
  onChange,
  exit,
}: {
  title: string
  value: AnimSpec
  onChange: (next: AnimSpec) => void
  exit?: boolean
}) {
  const { t } = useTranslation("editor")
  const set = (patch: Partial<AnimSpec>) => onChange({ ...value, ...patch })
  return (
    <Block
      $style={{
        border: "1px solid #e4e5ee",
        borderRadius: "10px",
        padding: "10px",
        marginBottom: "10px",
      }}
    >
      <Block $style={{ fontSize: "0.75rem", fontWeight: 600, marginBottom: "8px" }}>{title}</Block>
      <Block $style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
        {ANIM_TYPES.map((item) => (
          <Block key={item.id} onClick={() => set({ type: item.id as AnimSpec["type"] })} $style={chipStyle(value.type === item.id)}>
            {t(`panels.animations.${item.labelKey}`)}
          </Block>
        ))}
      </Block>

      {value.type !== "none" && (
        <>
          <Block $style={fieldRow}>
            <span>{t("panels.animations.durationSeconds")}</span>
            <input
              type="number"
              step={0.1}
              min={0.1}
              value={value.duration}
              onChange={(e) => set({ duration: Math.max(0.1, Number(e.target.value) || 0.1) })}
              style={inputStyle}
            />
          </Block>
          {!exit && (
            <Block $style={fieldRow}>
              <span>{t("panels.animations.delaySeconds")}</span>
              <input
                type="number"
                step={0.1}
                min={0}
                value={value.delay}
                onChange={(e) => set({ delay: Math.max(0, Number(e.target.value) || 0) })}
                style={inputStyle}
              />
            </Block>
          )}
          <Block $style={fieldRow}>
            <span>{t("panels.animations.easing")}</span>
            <select value={value.easing} onChange={(e) => set({ easing: e.target.value })} style={{ ...inputStyle, width: "110px" }}>
              {EASINGS.map((item) => (
                <option key={item.id} value={item.id}>
                  {t(`panels.animations.${item.labelKey}`)}
                </option>
              ))}
            </select>
          </Block>
          {value.type === "slide" && (
            <>
              <Block $style={fieldRow}>
                <span>{t("panels.animations.direction")}</span>
                <select value={value.direction} onChange={(e) => set({ direction: Number(e.target.value) })} style={{ ...inputStyle, width: "110px" }}>
                  {DIRECTIONS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {t(`panels.animations.${item.labelKey}`)}
                    </option>
                  ))}
                </select>
              </Block>
              <Block $style={fieldRow}>
                <span>{t("panels.animations.distance")}</span>
                <input
                  type="number"
                  step={5}
                  min={0}
                  value={value.distance}
                  onChange={(e) => set({ distance: Math.max(0, Number(e.target.value) || 0) })}
                  style={inputStyle}
                />
              </Block>
            </>
          )}
          {value.type === "scale" && (
            <Block $style={fieldRow}>
              <span>{exit ? t("panels.animations.endsAt") : t("panels.animations.startsAt")}</span>
              <input
                type="number"
                step={5}
                min={0}
                value={exit ? value.endScale : value.startScale}
                onChange={(e) =>
                  set(exit ? { endScale: Math.max(0, Number(e.target.value) || 0) } : { startScale: Math.max(0, Number(e.target.value) || 0) })
                }
                style={inputStyle}
              />
            </Block>
          )}
        </>
      )}
    </Block>
  )
}

export default function () {
  const editor = useEditor()
  const { t } = useTranslation("editor")
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const activeObject = useActiveObject<any>()
  const { animationPreset, setAnimationPreset } = useDesignEditorContext() as any
  const [enter, setEnter] = React.useState<AnimSpec>(DEFAULT_ENTER)
  const [exit, setExit] = React.useState<AnimSpec>(DEFAULT_EXIT)

  const objectId = activeObject?.id

  React.useEffect(() => {
    const hf = activeObject?.metadata?.hfAnimations
    setEnter({ ...DEFAULT_ENTER, ...(hf?.enter || {}) })
    setExit({ ...DEFAULT_EXIT, ...(hf?.exit || {}) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId])

  const applyToObject = React.useCallback(() => {
    if (!activeObject) return
    editor.objects.update({ metadata: { hfAnimations: { enter, exit } } })
  }, [editor, activeObject, enter, exit])

  const clearObject = React.useCallback(() => {
    if (!activeObject) return
    setEnter(DEFAULT_ENTER)
    setExit(DEFAULT_EXIT)
    editor.objects.update({ metadata: { hfAnimations: { enter: DEFAULT_ENTER, exit: DEFAULT_EXIT } } })
  }, [editor, activeObject])

  return (
    <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Block
        $style={{
          display: "flex",
          alignItems: "center",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "1.5rem",
        }}
      >
        <Block>{t("panels.animations.title")}</Block>
        <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
          <AngleDoubleLeft size={18} />
        </Block>
      </Block>

      <Scrollable>
        <Block padding={"0 1.5rem 2rem"}>
          <Block $style={{ fontSize: "0.8rem", opacity: 0.6, marginBottom: "0.75rem" }}>
            {t("panels.animations.hint")}
          </Block>

          {activeObject ? (
            <>
              <Block
                $style={{
                  fontSize: "0.72rem",
                  marginBottom: "8px",
                  padding: "6px 8px",
                  background: "#f4f5fa",
                  borderRadius: "6px",
                }}
              >
                {t("panels.animations.selected")} <b>{activeObject.type}</b>
              </Block>
              <AnimEditor title={t("panels.animations.enter")} value={enter} onChange={setEnter} />
              <AnimEditor title={t("panels.animations.exit")} value={exit} onChange={setExit} exit />
              <Block $style={{ display: "flex", gap: "8px", marginBottom: "1rem" }}>
                <Block
                  onClick={applyToObject}
                  $style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "8px",
                    borderRadius: "8px",
                    background: "#192bc2",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                  }}
                >
                  {t("panels.animations.apply")}
                </Block>
                <Block
                  onClick={clearObject}
                  $style={{
                    textAlign: "center",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d7d8e3",
                    color: "#333",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                  }}
                >
                  {t("panels.animations.clear")}
                </Block>
              </Block>
            </>
          ) : (
            <Block
              $style={{
                fontSize: "0.72rem",
                marginBottom: "1rem",
                padding: "8px",
                background: "#f4f5fa",
                borderRadius: "6px",
                opacity: 0.8,
              }}
            >
              {t("panels.animations.noSelection")}
            </Block>
          )}

          <Block $style={{ fontSize: "0.72rem", fontWeight: 600, marginBottom: "6px" }}>{t("panels.animations.presetLabel")}</Block>
          <Block $style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "0.5rem" }}>
            {PRESETS.map((item) => (
              <Block key={item.id} onClick={() => setAnimationPreset(item.id)} $style={chipStyle(animationPreset === item.id)}>
                {t(`panels.animations.${item.labelKey}`)}
              </Block>
            ))}
          </Block>
        </Block>
      </Scrollable>
    </Block>
  )
}
